import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, RefreshCw, X } from 'lucide-react';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function NetworkStatusBanner() {
  const { isOnline, wasOffline, checkConnection } = useNetworkStatus();
  const [showReconnectedBanner, setShowReconnectedBanner] = useState(false);
  const [checking, setChecking] = useState(false);
  const [dismissedOffline, setDismissedOffline] = useState(false);

  // When coming back online after having been offline
  useEffect(() => {
    if (isOnline && wasOffline) {
      setShowReconnectedBanner(true);
      const timer = setTimeout(() => {
        setShowReconnectedBanner(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  // Reset dismissal when offline state changes
  useEffect(() => {
    if (!isOnline) {
      setDismissedOffline(false);
    }
  }, [isOnline]);

  const handleRetry = async () => {
    setChecking(true);
    await checkConnection();
    setChecking(false);
  };

  // Reconnected notification
  if (showReconnectedBanner && isOnline) {
    return (
      <div
        className="fixed bottom-6 right-6 z-[999999] flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-600/95 px-4 py-3 text-white shadow-2xl backdrop-blur-xl animate-in slide-in-from-bottom-5 duration-300"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
          <Wifi className="h-4 w-4 text-white" />
        </div>
        <div>
          <p className="text-sm font-semibold">You're back online!</p>
          <p className="text-xs text-emerald-100">Syncing latest updates...</p>
        </div>
        <button
          onClick={() => setShowReconnectedBanner(false)}
          className="ml-2 rounded-full p-1 text-emerald-200 hover:bg-white/10 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Offline notification
  if (!isOnline && !dismissedOffline) {
    return (
      <div
        className={cn(
          'fixed bottom-6 left-1/2 -translate-x-1/2 z-[999999] w-[92%] max-w-md',
          'flex items-center justify-between gap-3 rounded-2xl border border-amber-500/30 bg-slate-900/95 p-3.5 sm:p-4 text-white shadow-2xl backdrop-blur-xl',
          'animate-in slide-in-from-bottom-5 duration-300'
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/20 text-amber-400 flex-shrink-0">
            <WifiOff className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">No internet connection</p>
            <p className="text-xs text-slate-400 truncate">Working offline. Changes will sync when reconnected.</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Button
            size="sm"
            variant="outline"
            disabled={checking}
            onClick={handleRetry}
            className="h-8 rounded-xl border-slate-700 bg-slate-800 text-xs text-slate-200 hover:bg-slate-700 hover:text-white px-2.5 gap-1"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', checking && 'animate-spin')} />
            <span className="hidden sm:inline">Retry</span>
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDismissedOffline(true)}
            className="h-8 w-8 p-0 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return null;
}

